import { useEffect, useState } from 'react';
import { Command } from 'cmdk';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/auth/authStore';
import { hasAccess } from '@/auth/roles';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { navSections } from '@/routes/nav';

/** ⌘K / Ctrl+K palette. Stub: navigation only; actions (create agent, open run…) come with later tasks. */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const items = navSections
    .flatMap((s) => s.items)
    .filter((i) => !i.access || (user && hasAccess(user.roles, user.caps, i.access)));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent aria-describedby={undefined}>
        <DialogTitle className="sr-only">{t('command.hint')}</DialogTitle>
        <Command label={t('command.hint')} className="flex flex-col">
          <Command.Input
            autoFocus
            placeholder={t('command.placeholder')}
            className="h-11 border-b bg-transparent px-3 text-sm outline-none"
          />
          <Command.List className="max-h-80 overflow-y-auto p-1">
            <Command.Empty className="p-3 text-sm text-muted-foreground">{t('command.empty')}</Command.Empty>
            <Command.Group heading={t('command.navigate')} className="text-xs text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1">
              {items.map((item) => (
                <Command.Item
                  key={item.to}
                  value={t(item.labelKey)}
                  onSelect={() => {
                    setOpen(false);
                    void navigate(item.to);
                  }}
                  className="flex cursor-default items-center gap-2 rounded px-2 py-1.5 text-sm text-foreground data-[selected=true]:bg-muted"
                >
                  <item.icon className="size-4 text-muted-foreground" />
                  {t(item.labelKey)}
                </Command.Item>
              ))}
            </Command.Group>
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
